import React, { useEffect, useMemo, useRef, useState } from 'react';
import './CalendarPicker.css';

export default function CalendarPicker({ open, onClose, endpoint, selectedDate, onChange }) {
  const [month, setMonth] = useState(new Date());
  const [settings, setSettings] = useState(null);
  const [availability, setAvailability] = useState({});
  const [loadingAvail, setLoadingAvail] = useState(false);
  const [error, setError] = useState('');
  const availabilityCacheRef = useRef({});
  const availReqIdRef = useRef(0);

  const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
  const endOfMonth = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
  const addDays = (d, n) => { const nd = new Date(d); nd.setDate(nd.getDate() + n); return nd; };
  const weekdayLabels = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
  const monthKeyFromDate = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

  // Get WP base
  const getWpBase = () => {
    try {
      const u = new URL(endpoint);
      return `${u.protocol}//${u.host}`;
    } catch {
      return '';
    }
  };
  const baseUrl = getWpBase();

  // Reset month if selectedDate changes externally and is valid
  useEffect(() => {
    if (open && selectedDate && /^\d{4}-\d{2}-\d{2}$/.test(selectedDate)) {
      const [y, m, d] = selectedDate.split('-').map(Number);
      setMonth(new Date(y, m - 1, d));
    }
  }, [open, selectedDate]);

  // Load Settings
  useEffect(() => {
    let cancelled = false;
    async function loadSettings() {
      if (!open || !baseUrl) return;
      try {
        const res = await fetch(`${baseUrl}/wp-json/sj/v1/settings`);
        const data = await res.json();
        if (!cancelled && data.window) setSettings(data);
      } catch (_e) {
        // Fallback or error handling
      }
    }
    loadSettings();
    return () => { cancelled = true; };
  }, [open, baseUrl]);

  const range = useMemo(() => {
    const start = startOfMonth(month);
    const end = endOfMonth(month);
    return { from: fmt(start), to: fmt(end) };
  }, [month]);

  // Load Availability
  useEffect(() => {
    let cancelled = false;
    async function loadAvailability() {
      if (!open || !baseUrl) return;
      setLoadingAvail(true);
      setError('');
      
      const mKey = monthKeyFromDate(month);
      const cached = availabilityCacheRef.current[mKey];
      if (cached) {
        setAvailability(cached);
        // Still fetch to update? No, simple cache is fine for now
      }

      const reqId = ++availReqIdRef.current;
      try {
        const res = await fetch(`${baseUrl}/wp-json/sj/v1/availability?from=${range.from}&to=${range.to}&_t=${Date.now()}`);
        if (!res.ok) throw new Error(`Status ${res.status}`);
        const data = await res.json();
        const map = {};
        for (const day of data.days || []) map[day.date] = { available: !!day.available, remaining: day.remaining ?? 0 };
        
        if (!cancelled && reqId === availReqIdRef.current) {
          availabilityCacheRef.current[mKey] = map;
          setAvailability(map);
        }
      } catch (err) {
        if (!cancelled && reqId === availReqIdRef.current) {
          console.error("Availability fetch error:", err);
          setError('Could not load availability. Please update the WordPress plugin.');
        }
      } finally {
        if (!cancelled && reqId === availReqIdRef.current) setLoadingAvail(false);
      }
    }
    loadAvailability();
    return () => { cancelled = true; };
  }, [open, baseUrl, range]);

  const daysForMonth = useMemo(() => {
    const s = startOfMonth(month);
    const e = endOfMonth(month);
    const startOffset = s.getDay();
    const total = startOffset + e.getDate();
    const rows = Math.ceil(total / 7);
    const grid = [];
    let cur = addDays(s, -startOffset);
    for (let r = 0; r < rows; r++) {
      const row = [];
      for (let c = 0; c < 7; c++) { row.push(new Date(cur)); cur = addDays(cur, 1); }
      grid.push(row);
    }
    return grid;
  }, [month]);

  const changeMonth = (delta) => {
    setMonth((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));
  };

  const handleSelect = (d) => {
    const key = fmt(d);
    const meta = availability[key];
    // Allow selection if available
    if (meta && meta.available && d.getMonth() === month.getMonth()) {
      onChange(key);
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="cp-overlay" onClick={onClose}>
      <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="cp-header">
          <h3 className="cp-title">Select Date</h3>
          <button type="button" className="cp-close" onClick={onClose}>×</button>
        </div>
        <div className="cp-content">
          <div className="calendar-header">
            <button type="button" className="cal-nav" onClick={() => changeMonth(-1)}>‹</button>
            <div className="cal-title">{month.toLocaleString(undefined, { month: 'long', year: 'numeric' })}</div>
            <button type="button" className="cal-nav" onClick={() => changeMonth(1)}>›</button>
          </div>
          <div className="calendar-weekdays">
            {weekdayLabels.map((w) => (<div key={w} className="calendar-weekday">{w}</div>))}
          </div>
          <div className="calendar-days" aria-busy={loadingAvail}>
            {loadingAvail && !error && <div className="cp-msg">Loading availability...</div>}
            {error && <div className="cp-error">{error}</div>}
            {!error && daysForMonth.map((row, rIdx) => (
              <div className="calendar-row" key={rIdx}>
                {row.map((d) => {
                  const key = fmt(d);
                  const inMonth = d.getMonth() === month.getMonth();
                  const meta = availability[key] || { available: false };
                  const isAvail = inMonth && meta.available;
                  const isSel = key === selectedDate;
                  
                  return (
                    <button
                      key={key}
                      type="button"
                      className={`day ${inMonth ? '' : 'muted'} ${isAvail ? 'avail' : 'blocked'} ${isSel ? 'selected' : ''}`}
                      onClick={() => handleSelect(d)}
                      disabled={!isAvail}
                    >
                      <span className="day-num">{d.getDate()}</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
          <div className="cp-legend">
            <div className="legend-item"><span className="dot avail"></span> Available</div>
            <div className="legend-item"><span className="dot blocked"></span> Full / Blocked</div>
          </div>
        </div>
      </div>
    </div>
  );
}
